import { ArrowUpRight, Mail } from "lucide-react";
import { SOCIALS } from "../lib/socials";
import { MaskedLine, FadeUp } from "./Reveal";

const FOOTER_LINKS = [
  { label: "What We Do", href: "#what-we-do" },
  { label: "Solutions", href: "#solutions" },
  { label: "Work", href: "#work" },
  { label: "Products", href: "#products" },
  { label: "Industries", href: "#industries" },
  { label: "About", href: "#about" },
  { label: "Insights", href: "#insights" },
  { label: "Contact", href: "#contact" },
  { label: "Privacy", href: "#" },
];

export const FinalCTA = ({ onContact }) => (
  <footer id="contact" data-testid="final-cta-section" className="bg-jx-night text-white relative overflow-hidden">
    <div className="pointer-events-none absolute -top-32 left-1/3 w-[600px] h-[400px] rounded-full bg-jx-magenta/15 blur-[140px]" />

    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-28 sm:pt-36 pb-14 relative">
      <p className="jx-overline text-jx-copper">Start the Conversation</p>
      <h2 className="mt-6 font-display font-extrabold tracking-tighter leading-[1.02] text-4xl sm:text-6xl lg:text-7xl max-w-4xl" data-testid="cta-headline">
        <MaskedLine>What could your business do</MaskedLine>
        <MaskedLine delay={0.1}>with <span className="jx-gradient-text">more intelligence?</span></MaskedLine>
      </h2>
      <FadeUp delay={0.25} className="mt-8 max-w-xl">
        <p className="text-base sm:text-lg text-white/55 leading-relaxed" data-testid="cta-subtext">
          Tell us what you are trying to build, improve or automate. We will help turn the opportunity into a working solution.
        </p>
      </FadeUp>
      <FadeUp delay={0.35} className="mt-10 flex flex-wrap items-center gap-5">
        <button
          data-testid="footer-cta-button"
          onClick={onContact}
          className="group inline-flex items-center gap-3 rounded-full jx-gradient-bg text-white font-medium px-8 py-4 shadow-[0_16px_44px_rgba(209,45,140,0.35)] transition-[transform,box-shadow] duration-300 hover:scale-[1.04] active:scale-95"
        >
          Start a Conversation
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
        <a
          data-testid="footer-email-link"
          href="mailto:hello@jiyonox.com"
          className="group inline-flex items-center gap-2.5 text-white/70 hover:text-white font-medium transition-colors duration-300"
        >
          <Mail className="w-4 h-4 text-jx-copper" />
          hello@jiyonox.com
          <span className="block h-px w-0 group-hover:w-full bg-white/40 transition-[width] duration-300" />
        </a>
      </FadeUp>

      <div className="mt-24 pt-10 border-t border-white/8 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <img src="/assets/logo-horizontal-light-sm.png" alt="JIYONOX" width="314" height="40" className="h-10 w-auto" loading="lazy" />
          <p className="mt-3 font-editorial italic text-white/60">From Intelligence to Impact.</p>
          <p className="mt-4 text-[11px] font-bold tracking-[0.3em] text-white/35">BUILD | OPERATE | AUTOMATE | GROW</p>
        </div>
        <div className="md:col-span-4 grid grid-cols-2 gap-x-6 gap-y-3">
          {FOOTER_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={(e) => {
                if (l.href.startsWith("#") && l.href.length > 1) {
                  e.preventDefault();
                  if (l.label === "Contact") onContact();
                  else document.querySelector(l.href)?.scrollIntoView({ behavior: "smooth" });
                } else if (l.href === "#") e.preventDefault();
              }}
              className="text-sm text-white/55 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </div>
        <div className="md:col-span-3">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/35 mb-4">Follow</p>
          <div className="flex flex-wrap gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                data-testid={`social-${s.label.toLowerCase()}`}
                className="w-10 h-10 rounded-full border border-white/12 flex items-center justify-center text-white/60 hover:text-white hover:border-jx-magenta/60 hover:bg-jx-magenta/10 transition-[color,border-color,background-color] duration-300"
              >
                <s.Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-14 flex flex-wrap items-center justify-between gap-4 text-xs text-white/30">
        <p data-testid="footer-copyright">© {new Date().getFullYear()} JIYONOX. All rights reserved.</p>
        <p>jiyonox.com</p>
      </div>
    </div>
  </footer>
);
